<template>
  <Teleport to="body">
    <div class="modal-overlay">
      <div
        class="modal-container"
        :class="{
          'modal-reactivate': action === 'reactivate',
          'modal-deactivate': action === 'deactivate',
        }"
        @click.stop
      >
        <div class="modal-header">
          <h2 class="modal-title">{{ pageTitle }}</h2>
          <button class="close-btn" type="button" aria-label="Close" @click="handleClose">
            <img :src="fillIcon" alt="" class="close-icon" />
          </button>
        </div>

        <div v-if="couponName" class="modal-body">
          <p class="check-message">{{ messageText }}</p>
        </div>

        <div v-else class="modal-body">
          <p class="error-text">Coupon not found</p>
        </div>

        <div class="modal-footer">
          <button class="btn-no" type="button" @click="handleClose">No</button>
          <button class="btn-yes" type="button" :disabled="submitting" @click="handleConfirm">
            <span v-if="submitting" class="spinner" aria-hidden="true"></span>
            <span v-else>Yes</span>
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, inject, ref } from 'vue'
import { isCouponMutationSuccess, readCouponApiMessage } from '@/api/couponApi'
import { postCouponSetStatus } from '@/api/CouponSetStatus'
import fillIcon from '@/assets/Fill 1@2x.png'

interface Props {
  couponId: string
  recordId: number
  couponName: string
  action: 'deactivate' | 'reactivate'
}

const props = defineProps<Props>()

const emit = defineEmits<{
  close: []
  saved: []
  failed: []
}>()

const showToast = inject<(type: 'success' | 'warning' | 'error', message: string) => void>(
  'showToast',
  () => undefined,
)

const submitting = ref(false)

const pageTitle = computed(() =>
  props.action === 'deactivate' ? 'Deactivate Coupon' : 'Reactivate Coupon',
)

const messageText = computed(() => {
  if (!props.couponName) return ''
  if (props.action === 'deactivate') {
    return `Are you sure you want to deactivate "${props.couponName}"? This coupon will no longer be available going forward. You can reactivate it if needed.`
  }
  return `Are you sure you want to reactivate "${props.couponName}"? This coupon will remain available.`
})

function handleClose(): void {
  emit('close')
}

async function handleConfirm(): Promise<void> {
  if (!props.recordId) {
    showToast('error', 'Coupon record ID is missing')
    emit('failed')
    return
  }

  submitting.value = true
  try {
    const response = await postCouponSetStatus({
      id: props.recordId,
      coupon_id: props.couponId,
      action: props.action,
    })

    if (isCouponMutationSuccess(response)) {
      emit('saved')
      return
    }

    showToast('error', readCouponApiMessage(response, 'Failed to update status'))
    emit('failed')
  } catch (error) {
    console.error('Update coupon status error:', error)
    showToast('error', 'Failed to update status')
    emit('failed')
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 11000;
}

.modal-container {
  background-color: #ffffff;
  border-radius: 12px;
  width: 530px;
  max-width: calc(100vw - 48px);
  min-height: 212px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  box-sizing: border-box;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-shrink: 0;
  padding: 24px;
  box-sizing: border-box;
}

.modal-title {
  font-size: 16px;
  font-weight: 700;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  margin: 0;
  line-height: 24px;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-right: 8px;
}

.close-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.modal-body {
  padding: 0 24px 24px;
  box-sizing: border-box;
}

.check-message {
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  margin: 0;
  word-break: break-word;
  overflow-wrap: break-word;
}

.error-text {
  font-size: 14px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  text-align: center;
  margin: 0;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  padding: 0 24px 24px;
  flex-shrink: 0;
  margin-top: auto;
}

.btn-no {
  width: 80px;
  height: 32px;
  padding: 0;
  border: 1px solid #000000;
  border-radius: 16px;
  background-color: #ffffff;
  color: #000000;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-no:hover {
  background-color: #000000;
  border-color: #000000 !important;
  color: #ffffff;
  outline: none;
}

.btn-yes {
  width: 80px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 16px;
  background-color: #000000;
  color: #ffffff;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-yes:hover:not(:disabled) {
  background-color: #484848;
}

.btn-yes:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
